/**
 * @format
 */

import React, { useRef, useMemo, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  SafeAreaView,
  StyleSheet,
  View,
} from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';
import { ScrollView } from 'react-native-gesture-handler';
import AsyncStorage from '@react-native-async-storage/async-storage';
import moment from 'moment';

import ThumbtackSearchResults from './ThumbtackSearchResults';
import { ResultsProps, RouteNames } from '../../navigation';
import * as Constant from '../../lib/constants';
import { RootState } from '../../store/rootReducer';
import SearchBar from '../../components/SearchBar';
import { SearchItemType, SearchResult } from '../../components/SearchBar/types';
import Divider from '../../components/Divider';
import CategoryAdvertList from '../../components/CategoryAdvertList';
import { selectCategoryAdvertsList } from '../../store/advertsSlice';
import { selectGatedCategoryAdvertsList } from '../../store/advertsGatedSlice';
import {
  Advertisement,
  isThumbtackPro,
  Tag,
  ThumbtackPro,
  fetchExploreProperty,
  ExploreProperty,
  PropertyType,
  // ThumbtackCategoriesObject,
  ThumbtackHomecare,
  isThumbtackCategory,
} from '../../api/adserver';
import Carousel, { DisplayType } from '../../components/Carousel';
import { selectCategories } from '../../store/advertCategoriesSlice';
import { trackScreenView } from '../../lib/analytics';
// import analyitics from '../../lib/wrapAnalytics';
import { clearCurrentQuery, showModal, hideModal } from '../../store/appSlice';
import AgeRestriction from '../../components/ageRestriction';

import { ModalName } from '../../components/RootOverlays';
import { getThumbtackPros, Provider } from '../../api/adserver';
import ThumbtackAdvertListItem from '../../components/CategoryAdvertList/CustomAdvertListItems';
import ExploreHeader from '../../web/components/ExploreHeader';

const debug = require('debug')('se:results-screen');

type Props = {} & ResultsProps;

interface TagsListProps {
  onSelectedListChange: (tags: Tag[]) => void;
  list?: any[];
  initialSelected: Tag[];
}

const UppperTagsList: React.FC<TagsListProps> = ({
  list,
  initialSelected,
  onSelectedListChange,
}) => {
  if (!list || !_.isArray(list) || list.length === 0) {
    <>
      <View style={[styles.tagsContainer]} />
      <Divider />
    </>;
  }
  return (
    <>
      <View style={[styles.tagsContainer]}>
        <Carousel
          name="resultstags"
          displayType={DisplayType.tags}
          items={list}
          onSelectedListChange={onSelectedListChange}
          initialSelected={initialSelected}
        />
      </View>
      <Divider />
    </>
  );
};

const ResultsScreen: React.FC<Props> = ({ navigation, route }: any) => {
  debug('ResultsScreen.render');
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { thumbtackCategories } = useSelector(
    (state: RootState) => state.advertCategories,
  );
  const { categories } = useSelector((state: RootState) =>
    selectCategories(state),
  );

  const age = route.params?.age || null;

  let initialValue = _.get(route, 'params.query', '');
  debug(`--------initialValue is:"${initialValue}"`);
  const [queryValue, setQueryValue] = useState(initialValue);
  debug(`--------queryValue is:"${queryValue}", age is: ${age}`);
  const provider: Provider = _.get(route, 'params.provider', Provider.Zonetail);
  debug(`--------provider is:"${provider}"`);

  let initialTagValue = _.get(route, 'params.tag', null);
  let intialTagsQueryValue: Tag[] = [];
  if (initialTagValue) {
    intialTagsQueryValue = [initialTagValue];
  } else {
    intialTagsQueryValue = [];
  }
  debug(`-------tag param is:${JSON.stringify(intialTagsQueryValue)}`);
  const [tagsQueryValue, setTagsQueryValue] =
    useState<Tag[]>(intialTagsQueryValue);

  const { advertsList, categoryAdvertList } = useSelector((state) =>
    age
      ? selectGatedCategoryAdvertsList(state as RootState)
      : selectCategoryAdvertsList(state as RootState),
  );

  const [hasSearchResults, setHasSearchResults] = useState(false);

  const [customAdverts, setCustomAdverts] = useState<ThumbtackPro[]>([]);
  const [currentValue, setCurrentValue] = useState(initialValue);
  const [searchText, setSearchText] = useState<string>();
  const [isApartmentSearch, setIsApartmentSearch] = useState<boolean>(false);
  const [currentProvider, setCurrentProvider] = useState<Provider>(provider);

  // For firebase analytics.
  // shows the category page name and id
  useEffect(() => {
    const currentCategory = categories.find((category) => {
      if (category.title === initialValue) {
        return true;
      }
      return false;
    });
    // if (currentCategory) {
    //   analyitics.trackAdvertCategory(initialValue, currentCategory?.id);
    // }
  }, [initialValue, categories]);

  // Get list of Thumbtack Pros
  useEffect(() => {
    const getThumbtackProsRes = async () => {
      try {
        setIsLoading(true);
        const res = await getThumbtackPros(currentValue);
        if (res) {
          setCustomAdverts(res);
        }
        setIsLoading(false);
      } catch (e) {
        console.log(e);
        setIsLoading(false);
      }
    };
    switch (currentProvider) {
      case Provider.Thumbtack: {
        getThumbtackProsRes().then(() =>
          console.log(`loaded thumbtack pros for: ${currentValue}`),
        );
        break;
      }
      case Provider.ThumbtackCategory: {
        setIsLoading(false);
        break;
      }
      case Provider.Zonetail: {
        setIsLoading(false);
        break;
      }
      default: {
        setIsLoading(false);
        break;
      }
    }
  }, [currentProvider, currentValue]);

  // debug('customAdverts', customAdverts);
  // debug('categoryAdvertList', categoryAdvertList);

  useEffect(() => {
    let section = '';
    switch (currentProvider) {
      case Provider.Zonetail:
        section = 'category_search';
        break;
      case Provider.Thumbtack:
        section = 'thumbtack_results';
        break;
      default:
        section = `${currentProvider}_search`;
        break;
    }
    trackScreenView(`${section}::${initialValue}`);
    setQueryValue(initialValue);
    dispatch(clearCurrentQuery());
  }, [initialValue, dispatch, currentProvider]);

  const listRef = useRef<{ scrollToTop(): () => {} }>(null);
  const [filteredList, setFilteredList] = useState<Advertisement[]>([]);
  useEffect(() => {
    // filteredList.current = categoryAdvertList.filter((category) => {
    //   return category.title === queryValue;
    // });
    // debug(
    //   '*** RE RENDERING FILTERED LIST.   Tags length is:',
    //   tagsQueryValue.length,
    // );
    const updatedFilteredList = categoryAdvertList.reduce<Advertisement[]>(
      (acc, category) => {
        // match the cateory name (from search)
        // match the tag names (from filters)
        // return a rebuilt list of adverts
        if (tagsQueryValue.length === 0 || tagsQueryValue[0].id === 'all') {
          if (category.title === queryValue) {
            acc.push(...category.adverts);
          }
        } else {
          if (category.title === queryValue) {
            const filteredAdverts = category.adverts.filter((advert) => {
              const res = _.differenceWith(
                tagsQueryValue,
                advert.tags,
                (a: Tag, b: Tag) => a.name === b.name,
              );
              return res.length === 0;
            });
            acc.push(...filteredAdverts);
          }
        }
        return acc;
      },
      [],
    );
    // debug(
    //   '*** FINISHED REBUILD OF FILTERED LIST.  current length is:',
    //   updatedFilteredList.length,
    // );
    setFilteredList(updatedFilteredList);
  }, [categoryAdvertList, tagsQueryValue, queryValue]);

  const [intialSelectedTags, setIntialSelectedTags] = useState<Tag[]>([]);

  const onCategoryClick = (
    item: ThumbtackHomecare,
    // item: ThumbtackCategoriesObject | ThumbtackHomecare,
  ) => {
    console.log('on category click', item.categoryName);
    const itemTitle = item.categoryName;
    navigation.push(RouteNames.Results, {
      query: itemTitle,
      provider: Provider.Thumbtack,
    });
  };

  const tagsList = useMemo<Tag[]>(() => {
    const masterList = categoryAdvertList.filter(
      (category) => category.title === queryValue,
    );
    const list = masterList.reduce<Tag[]>((acc, cat) => {
      const categoryTags = _.flatMap(cat.adverts, ({ tags }) => tags);
      return _.unionBy(acc, categoryTags, 'id');
    }, []);
    // add custom "All" to the front of the list
    list.unshift({ id: 'all', name: 'All' });
    let startValue = [list[0]];
    // if we got tags param via nav, then set it here
    if (initialTagValue) {
      startValue = [initialTagValue];
    }
    debug(`*******setting start tag ${JSON.stringify(startValue)}`);
    setIntialSelectedTags(startValue);
    return list;
  }, [categoryAdvertList, queryValue, initialTagValue]);

  const toggleOverlay = () => {
    dispatch(hideModal());
  };

  const onPress = (
    item: Advertisement | ThumbtackPro | ThumbtackHomecare,
    // | ThumbtackCategoriesObject,
  ) => {
    debug('item advert was pressed', item.id, item);
    let itemContext;
    if (isThumbtackPro(item)) {
      itemContext = {
        category: {
          title: currentValue,
        },
      };
    }
    if (!isThumbtackCategory(item)) {
      dispatch(
        showModal({
          modalName: ModalName.Advert,
          modalProps: {
            item,
            itemContext,
            onClose: handleOnClose,
            onPressTag: handleOnPressTag,
          },
          isShowModal: true,
        }),
      );
    } else {
      onCategoryClick(item);
    }
  };

  const handleOnClose = () => {
    debug('item advert was closed');
    toggleOverlay();
  };

  const onBackPressed = () => {
    debug('onBackPressed');
    navigation.goBack();
    // navigation.navigate(RouteNames.Explore);
  };

  const onSearch = (value: string) => {
    debug(`onSearch: value: ${value}`);
  };

  const onSearchFocus = async () => {
    let exploreProperty: ExploreProperty | undefined;
    try {
      const rawExploreProperty = await AsyncStorage.getItem(
        Constant.EXPLORE_PROPERTY,
      );
      exploreProperty = rawExploreProperty
        ? JSON.parse(rawExploreProperty)
        : null;
    } catch (err) {
      console.log(err);
    }

    if (!exploreProperty) {
      let condoCorpName;

      condoCorpName = await AsyncStorage.getItem(Constant.CONDO_CORP_NAME);

      if (condoCorpName) {
        exploreProperty = (await fetchExploreProperty(
          condoCorpName,
        )) as ExploreProperty;
        await AsyncStorage.setItem(
          Constant.EXPLORE_PROPERTY,
          JSON.stringify(exploreProperty),
        );
        console.log(`exploreProperty ${exploreProperty}`);
        console.dir(exploreProperty);
      }
    }

    if (
      currentProvider !== Provider.Thumbtack ||
      !exploreProperty ||
      (exploreProperty &&
        exploreProperty.property_type !== PropertyType.apartment)
    ) {
      return;
    }
    let getExpiryDate: moment.Moment | undefined;
    try {
      const getJsonExpiryDate = await AsyncStorage.getItem(
        Constant.SEARCH_BAR_POP_OVER,
      );
      getExpiryDate = getJsonExpiryDate ? JSON.parse(getJsonExpiryDate) : null;
    } catch (e) {
      console.log(e);
    }
    const currentTime = moment();
    let diff: number | undefined;

    if (getExpiryDate) {
      diff = currentTime!.diff(getExpiryDate, 'hours');
    }

    if (!getExpiryDate || (diff !== undefined && diff >= 0)) {
      const setExpiryDate = moment().add(24, 'hours');
      await AsyncStorage.setItem(
        Constant.SEARCH_BAR_POP_OVER,
        JSON.stringify(setExpiryDate),
      );
      setIsApartmentSearch(true);
    }
  };

  const handleOnSelectedTagListChange = (tags: Tag[]) => {
    debug('new list is', tags);
    // thumbtack doesn't use tag list anymore
    if (!_.isEqual(tags, tagsQueryValue)) {
      const filters = tags.map((tag) => tag.name).join(',');
      trackScreenView(`category_search_filter::${queryValue}::${filters}`);
      setTagsQueryValue(tags);
      listRef.current?.scrollToTop();
    } else {
      debug('tags array is unchanged.  not updating');
    }
  };

  debug(
    '*** RENDER READY.  current filtered list length:',
    filteredList.length,
  );

  const findAdvert = (item: SearchResult) => {
    const advert = advertsList.find(
      (advertElement) => advertElement.id === item.id,
    );
    return advert;
  };

  const onSearchResultPress = (item: SearchResult) => {
    if (item.itemType === SearchItemType.category) {
      debug(`-->results screen: navigating to query: ${item.title}`);
      setHasSearchResults(false);
      setCurrentValue(item.title);
      setTimeout(() => {
        navigation.navigate(RouteNames.Results, { query: item.title });
      }, 1);
    } else if (item.itemType === SearchItemType.advert) {
      const advert = findAdvert(item);
      if (advert) {
        onPress(advert);
      }
    }
  };

  const onSearchHasResults = (hasResults: boolean) => {
    setHasSearchResults(hasResults);
  };

  const handleOnPressTag = (tag: Tag) => {
    handleOnClose();
    debug(`handleOnPressTag: ${tag.id}: ${tag.name}`);
    handleOnSelectedTagListChange([tag]);
    setIntialSelectedTags([tag]);
  };

  return (
    <SafeAreaView style={styles.safeContainer}>
      {Platform.OS === 'web' ? <ExploreHeader /> : <View />}
      <View style={styles.container}>
        <AgeRestriction
          route={navigation.route}
          age={age}
          navigation={navigation}
        >
          <View>
            <SearchBar
              placeholder={
                currentProvider === Provider.Thumbtack ||
                currentProvider === Provider.ThumbtackCategory
                  ? 'Search Thumbtack Pros'
                  : 'Search'
              }
              initialValue={
                currentProvider === Provider.Zonetail ? currentValue : ''
              }
              enableBack
              onInputUpdate={onSearch}
              onBackPressed={onBackPressed}
              sourceCategories={categories}
              sourceAdvertsList={advertsList}
              onHasResults={onSearchHasResults}
              onResultPress={onSearchResultPress}
              onThumbtackSearchSubmit={setCustomAdverts}
              provider={currentProvider}
              setSearchText={setSearchText}
              onFocus={onSearchFocus}
              setProvider={setCurrentProvider}
              isApartmentSearch={isApartmentSearch}
              setIsApartmentSearch={setIsApartmentSearch}
            />
          </View>
          <View style={styles.scrollView}>
            {isLoading ? (
              <ActivityIndicator style={styles.loadingIndicator} />
            ) : null}
            {!hasSearchResults && !isLoading && (
              <ScrollView style={styles.scrollView}>
                <View style={styles.tagsOuterContainer}>
                  {currentProvider === Provider.Zonetail ? (
                    <UppperTagsList
                      list={tagsList}
                      initialSelected={intialSelectedTags}
                      onSelectedListChange={handleOnSelectedTagListChange}
                    />
                  ) : (
                    <ThumbtackSearchResults
                      searchResult={searchText}
                      categoryName={currentValue}
                      provider={currentProvider}
                    />
                  )}
                </View>
                <CategoryAdvertList
                  ref={listRef}
                  advertCategoryList={filteredList}
                  onPress={onPress}
                  provider={currentProvider}
                  customListItemComponent={ThumbtackAdvertListItem}
                  customListItemData={customAdverts}
                  thumbtackCategoryList={thumbtackCategories}
                />
              </ScrollView>
            )}
          </View>
        </AgeRestriction>
      </View>
    </SafeAreaView>
  );
};

const backgroundColor = '#333333';

const styles = StyleSheet.create({
  safeContainer: {
    flex: 1,
    ...Platform.select({
      web: {
        maxWidth: 1200,
        display: 'flex',
      },
      default: {},
    }),
  },
  container: {
    flex: 1,
    backgroundColor: backgroundColor,
  },
  tagsOuterContainer: {
    flex: 1,
    maxWidth: 1200,
    width: '100%',
    alignContent: 'flex-start',
    justifyContent: 'flex-start',
    alignSelf: 'flex-start',
  },
  advertListContainer: {},
  contentContainer: {
    flex: 1,
    marginStart: 15,
  },
  tagList: {
    height: 50,
    width: '100%',
    marginBottom: 10,
  },
  scrollView: {
    backgroundColor: '#ffffff',
    marginHorizontal: 0,
    flex: 1,
    // display: 'flex',
    // margin: 'auto',
    // width: '100%',
  },
  loadingIndicator: {
    marginTop: 20,
  },
  searchBarInput: {},
  searchBarInputContainer: {},
  searchBar: {},
  engine: {
    position: 'absolute',
    right: 0,
  },
  sectionContainer: {
    marginTop: 32,
    paddingHorizontal: 24,
  },
  tagsContainer: {
    marginTop: 5,
    marginBottom: 10,
    marginStart: 15,
    height: 40,
  },
  featureHeadingContainer: {
    flex: 1,
    flexDirection: 'row',
    marginBottom: 5,
    justifyContent: 'space-between',
    marginHorizontal: 10,
  },
  sectionStart: {
    marginTop: 20,
    marginBottom: 20,
  },
  sectionEnd: {
    marginBottom: 10,
  },
  featureHeading: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  featureViewAll: {
    fontSize: 14,
    fontWeight: 'bold',
    alignSelf: 'center',
  },
});

export default ResultsScreen;
